import { Box, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconMessageFilled } from '@zillow/constellation-icons';
import { useContext, useMemo } from 'react';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { MessageListEmptyContainerSkeleton } from '../../skeleton';
import EmptyState from '../../../EmptyState';
import { inboxPageIsLoadingStore, inboxPageShowEmptyContainerStore } from '../../../../lib/inboxSearchListStores';

const FOLDER_TEXT_OPTIONS = {
  [SatelliteConversationFolderTypeV2.ARCHIVED]: 'No messages in your archive',
  [SatelliteConversationFolderTypeV2.SPAM]: 'No messages in your spam',
  [SatelliteConversationFolderTypeV2.SHOW_ALL]: 'No messages in your inbox',
  [SatelliteConversationFolderTypeV2.INBOX]: 'No messages in your inbox',
  [SatelliteConversationFolderTypeV2.FAVORITE]: 'No starred messages',
};

export default function EmptyContainer() {
  const { renterProgressStatus, folder, readRepliedStatus, isByProperty } = useContext(InboxPageContext);
  const inboxPageIsLoading = inboxPageIsLoadingStore.useValue();
  const inboxPageShowEmptyContainer = inboxPageShowEmptyContainerStore.useValue();
  const hasFilters =
    isByProperty ||
    readRepliedStatus !== SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL ||
    renterProgressStatus !== SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL ||
    inboxPageShowEmptyContainer;
  const showMyPropertiesLink = !hasFilters && !inboxPageIsLoading && folder === SatelliteConversationFolderTypeV2.INBOX;
  const messageListText = useMemo(() => {
    switch (renterProgressStatus) {
      case SatelliteConversationRenterProgressStatusTypeV2.TENANTS:
        return 'Conversations with current tenants will be shown here';
      case SatelliteConversationRenterProgressStatusTypeV2.TOURS:
        return 'Once you have a scheduled tour with a renter, be sure to add a note to help organize your leads';
      case SatelliteConversationRenterProgressStatusTypeV2.APPLICANTS:
        return 'Invite promising renters to apply to your listing';
      case SatelliteConversationRenterProgressStatusTypeV2.INQUIRIES:
        return 'Inquiries from potential renters will be shown here';
      default:
        return FOLDER_TEXT_OPTIONS[folder];
    }
  }, [renterProgressStatus, folder]);

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        paddingInline: 'default',
        textAlign: 'center',
      }}
    >
      {inboxPageIsLoading ? (
        <MessageListEmptyContainerSkeleton />
      ) : (
        <EmptyState
          icon={<IconMessageFilled />}
          body={messageListText}
          cta={
            showMyPropertiesLink ? (
              <Paragraph>
                <TextButton asChild>
                  {/* oxlint-disable-next-line next/no-html-link-for-pages -- cross-app route (My Properties dashboard), not one of this app's own pages */}
                  <a href="/rental-manager/properties">Go to My Properties</a>
                </TextButton>
              </Paragraph>
            ) : undefined
          }
        />
      )}
    </Box>
  );
}
